import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const DEFAULTS = {
    organization_name: "Your Annuity Review",
    tagline: "yourannuityreview.com",
    logo_url: null,
    primary_color: "#0B192C",
    support_email: "reviews@yourannuityreview.com",
    support_phone: null,
    footer_disclaimer: "We do not provide tax or legal advice. Recommendations are based on information provided and a full suitability review. Results may vary.",
    hero_headline: "Stop Guessing About Your Annuity.",
    hero_subheadline: "Get a clear, simple breakdown of what you own, what it costs, and what your options are.",
    owner_name: null,
    owner_title: "Licensed Insurance Professional",
    owner_photo_url: null,
    owner_phone: null,
    owner_email: null,
    owner_bio: null,
    owner_calendly_link: null,
    owner_states: null,
    owner_credentials: null,
    show_owner_section: true,
};

const Ctx = createContext({ branding: DEFAULTS, refresh: () => {}, applyPreview: () => {} });

function hexToRgb(hex) {
    if (!hex) return null;
    const m = hex.replace("#", "").match(/.{2}/g);
    if (!m || m.length !== 3) return null;
    return `${parseInt(m[0], 16)} ${parseInt(m[1], 16)} ${parseInt(m[2], 16)}`;
}

function shadeRgb(rgbStr, amount = 30) {
    const [r, g, b] = rgbStr.split(" ").map((n) => Math.min(255, Math.max(0, parseInt(n, 10) + amount)));
    return `${r} ${g} ${b}`;
}

export function applyTheme(hex) {
    const rgb = hexToRgb(hex);
    if (rgb) {
        document.documentElement.style.setProperty("--brand-rgb", rgb);
        document.documentElement.style.setProperty("--brand-rgb-hover", shadeRgb(rgb, 28));
    }
}

export function BrandingProvider({ children }) {
    const [branding, setBranding] = useState(DEFAULTS);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/public/branding");
            setBranding({ ...DEFAULTS, ...data });
            applyTheme(data.primary_color || DEFAULTS.primary_color);
            if (data.organization_name) document.title = data.organization_name;
        } catch {
            applyTheme(DEFAULTS.primary_color);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // Live preview support — applies CSS + title without touching saved branding state
    const applyPreview = useCallback((partial) => {
        if (partial.primary_color) applyTheme(partial.primary_color);
        if (partial.organization_name) document.title = partial.organization_name;
    }, []);

    return <Ctx.Provider value={{ branding, refresh, applyPreview }}>{children}</Ctx.Provider>;
}

export function useBranding() { return useContext(Ctx); }
